
    import { setupDrawInteraction, getDrawSource, initDrawLayer, getActiveDraw, removeLastDrawPoint, cancelDrawOnly } from './src/addressSearch.js';
    import { getAddressAidsAvailability } from './src/addressAidsAvailability.js';
    import { createStyleFromColor, peekNextUnusedColor } from './src/colorUtils.js';
    import { setAddressSelectionProvider, bumpAddressSelectionRevision } from './src/loadBaseLayers.js';
    import { loadFromBackend, reRunSpatialQuery, queueSaveToBackend, setFeatureName } from './src/drawSummaryPanel.js';
    import { getAccessToken } from './src/securityUtil.js';
    import { API_URL } from './src/config.js';
    import { Modify } from 'ol/interaction';
    import { Collection } from 'ol';
    import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style';
    import { MultiPoint } from 'ol/geom';
    import { fromLonLat } from 'ol/proj';
    import { belgiumCoverage, BELGIUM_EXTENT } from './src/belgium.js';

    // Sync counts vanaf de backend naar onze features.
    // Reden: upstream's `loadFromBackend()` zet wel addressData op de feature,
    // maar de backend stuurt (voor onze applicatie) lege address_data terug.
    // De echte count zit in het top-level `count`-veld per drawing.
    // We matchen op array-index (Sevenn returnt in volgorde van seq).
    async function syncFeatureCountsFromBackend() {
      const drawSource = getDrawSource();
      if (!drawSource) {
        console.warn('[BB sync] no drawSource');
        return false;
      }
      const features = drawSource.getFeatures();
      if (!features.length) {
        console.log('[BB sync] no features yet');
        return false;
      }
      const token = getAccessToken();
      if (!token) {
        console.warn('[BB sync] no access token');
        return false;
      }
      try {
        const r = await fetch(`${API_URL}spatial-search/draw/load`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          console.warn('[BB sync] draw/load failed', r.status);
          return false;
        }
        const data = await r.json();
        if (!Array.isArray(data)) {
          console.warn('[BB sync] unexpected response shape', data);
          return false;
        }
        let updated = 0;
        data.forEach((item, idx) => {
          if (features[idx] && typeof item.count === 'number') {
            features[idx].set('count', item.count);
            updated++;
          }
        });
        console.log('[BB sync]', features.length, 'features, set count on', updated, '- first item:', data[0]);
        return updated > 0;
      } catch (e) {
        console.warn('[BB sync] failed:', e);
        return false;
      }
    }

    // Module-scoped modify interaction zodat we 'm kunnen opruimen
    let activeModifyInteraction = null;
    let editingFeature = null; // feature waar we momenteel op editen
    let originalFeatureStyle = null; // backup style om te restoren

    function formatEuro(n, decimals = 2) {
      return '€' + Number(n).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: decimals });
    }

    // Bewerkstijl: huidige stijl plus hoekpunten. Upstream zet na elke
    // wijziging de stijl opnieuw (label), dus dit moet herhaald kunnen worden.
    function applyEditStyle(feature) {
      if (!feature) return;
      const cur = feature.getStyle?.();
      const list = Array.isArray(cur) ? [...cur] : cur ? [cur] : [];
      if (!list.includes(vertexHandleStyle)) list.push(vertexHandleStyle);
      feature.setStyle(list);
    }

    function markEditButton(btn, on) {
      if (!btn) return;
      btn.dataset.mode = on ? 'on' : 'off';
      btn.style.background = on ? '#22c55e' : '#0D2B3E';
      btn.style.color = '#fff';
      const lbl = btn.querySelector('.bb-edit-label');
      if (lbl) lbl.textContent = on ? 'Klaar' : 'Bewerk';
      const icon = btn.querySelector('i');
      if (icon) icon.className = on ? 'bi bi-check-lg' : 'bi bi-pencil-square';
    }

    function stopEditing() {
      if (activeModifyInteraction && window.bbMap) {
        window.bbMap.removeInteraction(activeModifyInteraction);
      }
      activeModifyInteraction = null;
      if (editingFeature) {
        const cur = editingFeature.getStyle?.();
        if (Array.isArray(cur)) editingFeature.setStyle(cur.filter((st) => st !== vertexHandleStyle));
        else if (originalFeatureStyle !== null) editingFeature.setStyle(originalFeatureStyle);
      }
      editingFeature = null;
      originalFeatureStyle = null;
      document.querySelectorAll('.bb-edit-btn').forEach((b) => markEditButton(b, false));
      setHint(currentDrawType ? (HINTS[currentDrawType] || '') : '');
      updateCursorClass();
    }

    // Style voor de vertex-handles (zichtbare punten op elk hoekpunt)
    const vertexHandleStyle = new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#ffffff' }),
        stroke: new Stroke({ color: '#0D2B3E', width: 2 }),
      }),
      geometry: function (feature) {
        const geom = feature.getGeometry();
        if (!geom) return null;
        const type = geom.getType();
        if (type === 'Polygon') {
          // alle ringen samenvoegen tot één MultiPoint
          const all = [];
          geom.getCoordinates().forEach((ring) => ring.forEach((c) => all.push(c)));
          return new MultiPoint(all);
        }
        if (type === 'MultiPolygon') {
          const all = [];
          geom.getCoordinates().forEach((poly) =>
            poly.forEach((ring) => ring.forEach((c) => all.push(c))),
          );
          return new MultiPoint(all);
        }
        if (type === 'LineString') return new MultiPoint(geom.getCoordinates());
        if (type === 'Circle') {
          const c = geom.getCenter();
          return new MultiPoint([c, [c[0] + geom.getRadius(), c[1]]]);
        }
        return null;
      },
    });

    let bbSearchTimeout = null;

    // In de wizard (iframe) tonen we geen technische statusbalk.
    if (new URLSearchParams(location.search).get('embed') === 'true') {
      document.body.classList.add('bb-embed');
    }

    // ── Teken-helpers (module-scope, ook nodig voor de automatische start) ──
    let currentDrawType = null;   // 'Polygon' | 'Circle' | null
    let sketchActive = false;     // er staat een onafgewerkte schets op de kaart
    let autoStarted = false;      // polygoon is al automatisch gestart

    function setActiveDrawBtn(activeId) {
      ['bb-draw-btn', 'bb-draw-radius-btn'].forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.classList.toggle('active', id === activeId);
      });
    }

    function startDrawWithColor(type) {
      const colorEl = document.getElementById('bb-next-color');
      const color = colorEl?.value || '#FF0000';
      setupDrawInteraction(type, color);
      setActiveDrawBtn(type === 'Circle' ? 'bb-draw-radius-btn' : 'bb-draw-btn');
    }

    function setHint(text, warn = false) {
      const el = document.getElementById('bb-hint');
      if (!el) return;
      // Een lopende melding niet overschrijven door de gewone tekentip
      // (bv. bij het opnieuw klaarzetten van het tekengereedschap).
      if (!warn && noticeTimer) return;
      el.textContent = text || '';
      el.style.display = text ? 'block' : 'none';
      el.classList.toggle('bb-hint-warn', Boolean(text && warn));
    }

    // Tijdelijke melding (bv. selectie buiten België); daarna komt de
    // gewone tekentip terug.
    let noticeTimer = null;
    function currentHint() {
      if (editingFeature) return editHintFor(editingFeature);
      return currentDrawType ? (HINTS[currentDrawType] || '') : '';
    }
    function notice(text) {
      document.dispatchEvent(new CustomEvent('bb-notice', { detail: { text } }));
    }
    document.addEventListener('bb-notice', (e) => {
      const text = e.detail?.text;
      if (!text) return;
      clearTimeout(noticeTimer);
      noticeTimer = null;
      setHint(text, true);
      noticeTimer = setTimeout(() => {
        noticeTimer = null;
        setHint(currentHint());
      }, 6000);
    });

    const HINTS = {
      Polygon: 'Klik om punten te plaatsen. Sluit de vorm door op het beginpunt te klikken of te dubbelklikken. Slepen verplaatst de kaart.',
      Circle: 'Klik op het middelpunt en sleep tot de gewenste straal.',
      edit: 'Sleep een punt om het gebied aan te passen. Klik op een lijn om een punt toe te voegen. Klik op Klaar als je klaar bent.',
      editCircle: 'Sleep de rand om de straal aan te passen, of het middelpunt om de cirkel te verplaatsen. Klik op Klaar als je klaar bent.',
    };
    function editHintFor(feature) {
      return feature?.getGeometry?.()?.getType?.() === 'Circle' ? HINTS.editCircle : HINTS.edit;
    }

    function refreshDrawTools() {
      const tools = document.getElementById('bb-draw-tools');
      const undo = document.getElementById('bb-undo-point');
      if (tools) tools.style.display = currentDrawType === 'Polygon' ? 'flex' : 'none';
      if (undo) undo.disabled = !sketchActive;
    }

    document.addEventListener('bb-draw-armed', (e) => {
      currentDrawType = e.detail?.type || null;
      if (!currentDrawType) {
        setActiveDrawBtn(null);
        if (!editingFeature) setHint('');
      } else {
        setHint(HINTS[currentDrawType] || '');
      }
      sketchActive = false;
      refreshDrawTools();
      updateCursorClass();
    });
    // Klik op een bestaand gebied terwijl er nog geen punt staat: niet
    // beginnen tekenen, maar dat gebied selecteren in de lijst rechts.
    let selectedFeature = null;
    function selectArea(feature) {
      selectedFeature = feature || null;
      applySelectedRow();
    }
    function applySelectedRow() {
      document.querySelectorAll('.bb-selection-item.bb-selected').forEach((el) => el.classList.remove('bb-selected'));
      if (!selectedFeature) return;
      const src = getDrawSource();
      const idx = src ? src.getFeatures().indexOf(selectedFeature) : -1;
      if (idx < 0) { selectedFeature = null; return; }
      const row = document.querySelector(`.bb-selection-item[data-id="${idx}"]`);
      if (row) { row.classList.add('bb-selected'); row.scrollIntoView({ block: 'nearest' }); }
    }
    function areaAtCoordinate(coordinate) {
      const src = getDrawSource();
      if (!src || !coordinate) return null;
      return src.getFeatures().find((f) => f.getGeometry()?.intersectsCoordinate?.(coordinate)) || null;
    }

    document.addEventListener('bb-draw-sketch', (e) => {
      const active = !!e.detail?.active;
      if (active && !editingFeature) {
        const hit = areaAtCoordinate(e.detail?.coordinate);
        if (hit) {
          try { getActiveDraw()?.abortDrawing?.(); } catch { /* geen schets */ }
          selectArea(hit);
          return;
        }
      }
      sketchActive = active;
      refreshDrawTools();
    });

    // ── Cursor naargelang de context ──
    const CURSOR_CLASSES = ['bb-cursor-draw', 'bb-cursor-close', 'bb-cursor-vertex', 'bb-cursor-segment'];
    let cursorClass = null;
    function updateCursorClass(next = null) {
      if (!window.bbMap) return;
      const target = window.bbMap.getTargetElement();
      if (!target) return;
      const wanted = next || (currentDrawType ? 'bb-cursor-draw' : null);
      if (wanted === cursorClass) return;
      CURSOR_CLASSES.forEach((c) => target.classList.remove(c));
      if (wanted) target.classList.add(wanted);
      cursorClass = wanted;
    }

    function pixelDistance(map, coordA, pixelB) {
      const pa = map.getPixelFromCoordinate(coordA);
      if (!pa) return Infinity;
      return Math.hypot(pa[0] - pixelB[0], pa[1] - pixelB[1]);
    }

    function flatRingCoords(geom) {
      const type = geom?.getType?.();
      if (type === 'Polygon') return geom.getCoordinates().flat();
      if (type === 'MultiPolygon') return geom.getCoordinates().flat(2);
      if (type === 'LineString') return geom.getCoordinates();
      return [];
    }

    function cursorForPointer(map, evt) {
      if (editingFeature) {
        const geom = editingFeature.getGeometry();
        if (geom?.getType?.() === 'Circle') {
          // Een straal heeft geen punten om toe te voegen: rand = straal
          // aanpassen, middelpunt = verplaatsen. Allebei met de verplaats-cursor.
          const center = geom.getCenter();
          const edge = geom.getClosestPoint(evt.coordinate);
          if (pixelDistance(map, center, evt.pixel) <= 10 || (edge && pixelDistance(map, edge, evt.pixel) <= 8)) return 'bb-cursor-vertex';
          return null;
        }
        if (geom) {
          const nearVertex = flatRingCoords(geom).some((c) => pixelDistance(map, c, evt.pixel) <= 8);
          if (nearVertex) return 'bb-cursor-vertex';
          const closest = geom.getClosestPoint(evt.coordinate);
          if (closest && pixelDistance(map, closest, evt.pixel) <= 6) return 'bb-cursor-segment';
        }
        return null;
      }
      if (currentDrawType === 'Polygon' && sketchActive) {
        const draw = getActiveDraw();
        const sketch = draw?.getOverlay?.()?.getSource?.()?.getFeatures?.() || [];
        const poly = sketch.map((f) => f.getGeometry()).find((g) => g?.getType?.() === 'Polygon');
        const ring = poly?.getCoordinates?.()?.[0] || [];
        if (ring.length >= 5 && pixelDistance(map, ring[0], evt.pixel) <= 12) return 'bb-cursor-close';
      }
      return currentDrawType ? 'bb-cursor-draw' : null;
    }

    // ── Zoom-availability: teken-knoppen gated op layer-visibility ─────
    // Sevenn sta tekenen alleen toe als PC4/PC5/PC6 of address-layer
    // visible is op huidige zoom. We syncen onze knoppen + tonen een
    // permanente waarschuwing in het panel (los van selectie-state).
    function updateBBDrawAvailability(map) {
      if (!map) return;
      const av = getAddressAidsAvailability(map);
      const polyBtn = document.getElementById('bb-draw-btn');
      const radBtn = document.getElementById('bb-draw-radius-btn');
      const warnEl = document.getElementById('bb-zoom-warning');

      const reasonNL = av.allowed
        ? ''
        : 'Zoom verder in op de kaart om te kunnen tekenen.';

      [polyBtn, radBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !av.allowed;
        btn.classList.toggle('bb-disabled', !av.allowed);
        if (!av.allowed) {
          btn.setAttribute('title', reasonNL);
        } else {
          btn.removeAttribute('title');
        }
      });

      // Permanente waarschuwing, los van selecties
      if (warnEl) {
        warnEl.style.display = av.allowed ? 'none' : 'block';
      }

      // Standaard staat 'Gebied tekenen' aan zodra tekenen mag, zodat de
      // klant niet eerst een gereedschap hoeft te kiezen. Niet tijdens het
      // bewerken van een gebied.
      if (av.allowed && !autoStarted && !editingFeature) {
        autoStarted = true;
        startDrawWithColor('Polygon');
      }
      if (!av.allowed) autoStarted = false;
    }

    // Wait for map to be ready
    document.addEventListener('map-ready', (e) => {
      const map = e.detail;
      window.bbMap = map; // Store globally for edit buttons
      console.log('[BB] Map ready, initializing bewonersbrief mode');

      // Meteen heel België in beeld, zonder zoomanimatie. De kaart kent
      // zijn afmeting nog niet bij het eerste event, vandaar de fallback
      // op de grootte van het kaartvak.
      const mapEl = document.getElementById('map');
      map.getView().fit(BELGIUM_EXTENT, {
        size: map.getSize() || [mapEl?.clientWidth || 1200, mapEl?.clientHeight || 800],
        padding: [16, 16, 16, 16],
        duration: 0,
      });

      // Initialize draw layer
      initDrawLayer();

      // Upstream main.js doet zelf al `loadFromBackend()` bij init -
      // wij hoeven die niet opnieuw aan te roepen (zou "Skipping duplicate
      // load" geven). Wel wachten tot de features daadwerkelijk in de
      // drawSource zitten, dan onze count-sync uitvoeren.
      (async () => {
        const drawSource = getDrawSource();
        if (!drawSource) {
          console.warn('[BB] no drawSource at map-ready');
          return;
        }

        // Listener-based: zodra een feature wordt toegevoegd, sync (debounced)
        let syncTimer = null;
        const scheduleSync = () => {
          if (syncTimer) clearTimeout(syncTimer);
          syncTimer = setTimeout(async () => {
            await syncFeatureCountsFromBackend();
            updateBBPanel();
          }, 250);
        };
        drawSource.on('addfeature', scheduleSync);

        // Voor het geval features al ingeladen zijn (race winnen we niet altijd):
        // poll tot 10s of tot we features zien
        for (let i = 0; i < 20; i++) {
          await new Promise((r) => setTimeout(r, 500));
          const fs = drawSource.getFeatures();
          if (fs.length > 0) {
            console.log('[BB] features visible after', (i + 1) * 500, 'ms:', fs.length);
            await syncFeatureCountsFromBackend();
            updateBBPanel();
            return;
          }
        }
        console.log('[BB] no features after 10s - sessie zonder bestaande drawings');
      })();

      // Initial availability + live update bij zoom change
      updateBBDrawAvailability(map);
      map.getView().on('change:resolution', () => updateBBDrawAvailability(map));
      // Ook layer-visibility kan wijzigen (bv. user toggle in legend)
      map.getLayers().on('change:length', () => updateBBDrawAvailability(map));
      document.addEventListener('address-aids-unavailable', () => updateBBDrawAvailability(map));

      // Gebied tekenen / Straal selecteren
      document.getElementById('bb-draw-btn').addEventListener('click', () => {
        if (document.getElementById('bb-draw-btn').disabled) return;
        stopEditing();
        startDrawWithColor('Polygon');
      });
      document.getElementById('bb-draw-radius-btn').addEventListener('click', () => {
        if (document.getElementById('bb-draw-radius-btn').disabled) return;
        stopEditing();
        startDrawWithColor('Circle');
      });
      document.getElementById('bb-undo-point').addEventListener('click', () => removeLastDrawPoint());
      document.getElementById('bb-cancel-draw').addEventListener('click', () => {
        cancelDrawOnly();
        if (!editingFeature) startDrawWithColor('Polygon');
      });

      // Elk nieuw gebied krijgt automatisch een volgende kleur uit het palet.
      const drawSourceForColors = getDrawSource();
      if (drawSourceForColors) {
        drawSourceForColors.on('addfeature', () => {
          const used = drawSourceForColors.getFeatures().map((f) => {
            const st = f.getStyle?.();
            const one = Array.isArray(st) ? st[0] : st;
            return one?.getStroke?.()?.getColor?.();
          }).filter((c) => typeof c === 'string');
          const picker = document.getElementById('bb-next-color');
          if (picker) picker.value = peekNextUnusedColor(used);
        });

        // Adressen binnen een gebied oplichten op de adreslaag.
        setAddressSelectionProvider(() => drawSourceForColors.getFeatures().map((f) => f.getGeometry()).filter(Boolean));
        ['addfeature', 'removefeature', 'changefeature', 'clear'].forEach((ev) =>
          drawSourceForColors.on(ev, () => bumpAddressSelectionRevision()));
      }

      // Cursor en hover-koppeling kaart -> lijst
      map.on('pointermove', (evt) => {
        if (evt.dragging) return;
        updateCursorClass(cursorForPointer(map, evt));
        const src = getDrawSource();
        let hovered = null;
        if (src && !sketchActive && !editingFeature) {
          map.forEachFeatureAtPixel(evt.pixel, (f, layer) => { if (layer?.getSource?.() === src) { hovered = f; return true; } return false; }, { hitTolerance: 2 });
        }
        document.querySelectorAll('.bb-selection-item.bb-hover').forEach((el) => el.classList.remove('bb-hover'));
        if (hovered) {
          const idx = src.getFeatures().indexOf(hovered);
          const row = document.querySelector(`.bb-selection-item[data-id="${idx}"]`);
          if (row) { row.classList.add('bb-hover'); row.scrollIntoView({ block: 'nearest' }); }
        }
      });
      map.on('pointerdrag', () => map.getTargetElement()?.classList.add('bb-cursor-grabbing'));
      map.getViewport().addEventListener('pointerup', () => map.getTargetElement()?.classList.remove('bb-cursor-grabbing'));
      map.getViewport().addEventListener('pointerleave', () => map.getTargetElement()?.classList.remove('bb-cursor-grabbing'));

      // Richtprijs uit de wizard (parent) tonen onder het totaal.
      window.addEventListener('message', (evt) => {
        if (evt.source !== window.parent) return;
        const d = evt.data;
        if (!d || d.type !== 'bb-price') return;
        const el = document.getElementById('bb-price');
        if (!el) return;
        if (d.mode === 'totaal' && typeof d.subtotaal === 'number') {
          el.innerHTML = `Richtprijs <strong>${formatEuro(d.subtotaal)}</strong> excl. BTW<br><span style="color:#666;">Op basis van je huidige keuzes in de wizard.</span>`;
          el.style.display = 'block';
        } else if (d.mode === 'vanaf' && typeof d.perAdres === 'number') {
          el.innerHTML = `Verzending vanaf <strong>${formatEuro(d.perAdres, 4)}</strong> per adres<br><span style="color:#666;">De uiteindelijke prijs hangt af van bedrukking en envelop.</span>`;
          el.style.display = 'block';
        } else {
          el.style.display = 'none';
        }
      });
      
      // Upstream fires dit event elke keer dat drawHistory muteert (add,
      // edit-vertex). Vroeger keken we via MutationObserver naar
      // #draw-summary-panel, maar dat panel wordt lazy aangemaakt door
      // upstream's renderPanel - bij map-ready bestaat het nog niet, dus
      // observer.observe(null) faalde stil. Gevolg: nieuw-getekend gebied
      // toonde count 0 in de zijbalk tot de setInterval-tick (of tot een
      // volgende teken-actie triggerde). Event-based is deterministisch.
      document.addEventListener('draw-summary-updated', () => {
        updateBBPanel();
        if (editingFeature) applyEditStyle(editingFeature);
      });
      
      // Also check periodically for the panel (but not when editing)
      setInterval(() => {
        // Skip update if user is editing a name
        if (document.querySelector('#bb-selection-list input')) return;
        
        const panel = document.getElementById('draw-summary-panel');
        if (panel) {
          updateBBPanel();
        }
      }, 1000);
      
      // Search
      const searchInput = document.getElementById('bb-search-input');
      searchInput.addEventListener('input', () => {
        clearTimeout(bbSearchTimeout);
        bbSearchTimeout = setTimeout(() => handleBBSearch(searchInput.value, map), 300);
      });
    });
    
    function updateBBPanel() {
      const drawSource = getDrawSource();
      if (!drawSource) return;
      
      const features = drawSource.getFeatures();
      const listEl = document.getElementById('bb-selection-list');
      const noSelection = document.getElementById('bb-no-selection');
      const totalCountEl = document.getElementById('bb-total-count');
      const totalValueEl = document.getElementById('bb-total-value');
      
      let totalAddresses = 0;
      const items = features.map((feature, idx) => {
        const addressData = feature.get('addressData') || [];
        const style = feature.getStyle?.();
        // OL style kan een Array zijn (bv. tijdens edit-mode met vertex-style erbij).
        // Pak de eerste Style die een Stroke heeft voor de kleur-extractie.
        const styleForColor = Array.isArray(style) ? style[0] : style;
        const color = styleForColor?.getStroke?.()?.getColor?.() || '#e53935';
        // Bij draw-save zit count in addressData.length. Bij reload via
        // /draw/load is addressData leeg en zit de count in feature.get('count').
        const cachedCount = feature.get('count');
        const count = typeof cachedCount === 'number' && cachedCount > 0
          ? cachedCount
          : addressData.length;
        totalAddresses += count;
        return { id: idx, color, count, feature, addressData };
      });
      
      // Helper: convert various color formats (rgba/array/named) naar hex
      function toHex(c) {
        if (!c) return '#e53935';
        if (typeof c === 'string' && c.startsWith('#') && c.length >= 7) return c.slice(0, 7);
        // OL kan kleur als array [r,g,b,a] returnen
        if (Array.isArray(c) && c.length >= 3) {
          const h = (n) => Math.max(0, Math.min(255, n | 0)).toString(16).padStart(2, '0');
          return `#${h(c[0])}${h(c[1])}${h(c[2])}`;
        }
        return '#e53935';
      }

      // Vaste volgorde op volgnummer: upstream zet een bewerkt gebied
      // achteraan in de bron, en dan zou de rij van plaats wisselen.
      // `id` blijft de index in de bron (nodig voor upstream-knoppen).
      const rows = [...items].sort((a, b) => (a.feature.get('selectionNumber') || 0) - (b.feature.get('selectionNumber') || 0));
      listEl.innerHTML = rows.map(s => {
        // Zelfde naam als het label op de kaart: eigen naam, anders het vaste volgnummer.
        const name = s.feature.get('gebiedNaam') || `Gebied ${s.feature.get('selectionNumber') ?? s.id + 1}`;
        const hexColor = toHex(s.color);
        return `
        <div class="bb-selection-item" data-id="${s.id}" style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #eee;">
          <div style="display:flex;align-items:center;gap:10px;flex:1;min-width:0;">
            <input type="color"
                   class="bb-color-picker"
                   data-id="${s.id}"
                   value="${hexColor}"
                   title="Kies kleur voor deze zone"
                   style="width:28px;height:28px;border:1px solid #ddd;border-radius:4px;cursor:pointer;padding:0;background:transparent;flex-shrink:0;" />
            <div style="min-width:0;">
              <div class="bb-name" data-id="${s.id}" style="font-size:13px;font-weight:500;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="Klik om de naam te wijzigen">${name} <i class="bi bi-pencil bb-name-pencil"></i></div>
              <div style="font-size:12px;color:#666;">${s.count.toLocaleString()} adressen</div>
            </div>
          </div>
          <div style="display:flex;gap:6px;flex-shrink:0;align-items:center;">
            <button class="bb-edit-btn" data-id="${s.id}" data-mode="off"
              style="background:#0D2B3E;color:#fff;border:none;cursor:pointer;padding:6px 10px;border-radius:4px;font-size:12px;font-weight:600;display:inline-flex;align-items:center;gap:6px;"
              title="Pas de vorm aan op de kaart">
              <i class="bi bi-pencil-square"></i>
              <span class="bb-edit-label">Bewerk</span>
            </button>
            <button class="bb-zoom-btn" data-id="${s.id}" style="background:none;border:1px solid #FFD100;cursor:pointer;padding:5px 8px;border-radius:4px;color:#0D2B3E;" title="Zoom naar gebied">
              <i class="bi bi-search"></i>
            </button>
            <button class="bb-delete-btn" data-id="${s.id}" style="background:none;border:1px solid #dc3545;cursor:pointer;padding:5px 8px;border-radius:4px;color:#dc3545;" title="Verwijderen">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      `}).join('');

      // Bind color pickers - delegeer naar upstream's verborgen picker in
      // #draw-summary-panel zodat upstream's oninput ook queueSaveToBackend
      // triggert. Zonder die save blijft de nieuwe kleur alleen client-side
      // (niet persistent naar Sevenn + de wizard krijgt geen postMessage).
      listEl.querySelectorAll('.bb-color-picker').forEach(picker => {
        picker.addEventListener('input', (e) => {
          const id = parseInt(picker.dataset.id);
          const feature = items[id]?.feature;
          if (!feature) return;
          const newColor = e.target.value;

          const upstreamPicker = document.querySelector(
            `#draw-summary-panel .color-picker[data-index="${id}"]`
          );
          if (upstreamPicker) {
            upstreamPicker.value = newColor;
            upstreamPicker.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            // Fallback zonder upstream save - kleur wordt niet gepersisteerd
            console.warn('[BB] upstream color-picker niet gevonden, fallback');
            feature.setStyle(createStyleFromColor(newColor, feature.get('gebiedNaam') || feature.get('selectionNumber') || ''));
            map.render();
          }
        });
      });

      applySelectedRow();

      // Bewerkmodus blijft zichtbaar zolang ze actief is, ook na een
      // herrender van de lijst (die gebeurt na elke wijziging).
      if (editingFeature) {
        const idx = items.findIndex((it) => it.feature === editingFeature);
        markEditButton(listEl.querySelector(`.bb-edit-btn[data-id="${idx}"]`), true);
        applyEditStyle(editingFeature);
        setHint(editHintFor(editingFeature));
      }

      // Bind edit-knoppen - toggle Modify-interaction op die feature.
      // Bij activeren: auto-zoom naar de zone zodat editing comfortabel is.
      listEl.querySelectorAll('.bb-edit-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = parseInt(btn.dataset.id);
          const feature = items[id]?.feature;
          if (!feature || !window.bbMap) return;

          // Was deze knop al actief? Tweede klik = klaar.
          if (btn.dataset.mode === 'on') {
            stopEditing();
            if (!currentDrawType) startDrawWithColor('Polygon');
            return;
          }

          // Andere mogelijk-actieve edit opruimen, tekenen pauzeren, dan deze starten
          stopEditing();
          cancelDrawOnly();

          // 1. Auto-fit naar de zone met minimum zoom-niveau zodat
          //    Sevenn's address-aids beschikbaar blijven én de vertices
          //    fysiek goed klikbaar zijn.
          const view = window.bbMap.getView();
          const extent = feature.getGeometry().getExtent();
          view.fit(extent, {
            padding: [80, 80, 80, 80],
            duration: 350,
            maxZoom: 18, // niet absurd dichtbij
          });
          // Forceer minimum zoom voor editing (vertices moeten klikbaar zijn)
          setTimeout(() => {
            const z = view.getZoom();
            if (typeof z === 'number' && z < 15) {
              view.setZoom(15);
            }
          }, 400);

          // 2. Vertex-handles tonen: huidige stijl plus hoekpunten.
          editingFeature = feature;
          originalFeatureStyle = feature.getStyle?.() ?? null;
          applyEditStyle(feature);
          setHint(editHintFor(feature));

          // 3. Modify-interaction activeren
          activeModifyInteraction = new Modify({
            features: new Collection([feature]),
          });
          window.bbMap.addInteraction(activeModifyInteraction);

          // 4. Visuele active-state: knop wordt groen + label wisselt
          markEditButton(btn, true);

          // 5. Bij elke modifyend: re-count via spatial-search + persistent
          //    opslaan naar Sevenn (anders is de wijziging weg na reload en
          //    krijgt de wizard geen /draw/save postMessage).
          // Geometrie vóór de wijziging, om een versleping naar buiten
          // België terug te draaien.
          let geometryBeforeEdit = null;
          activeModifyInteraction.on('modifystart', () => {
            geometryBeforeEdit = feature.getGeometry()?.clone() || null;
          });
          activeModifyInteraction.on('modifyend', async () => {
            if (belgiumCoverage(feature.getGeometry()) === 'outside') {
              if (geometryBeforeEdit) feature.setGeometry(geometryBeforeEdit);
              applyEditStyle(feature);
              notice('Een gebied moet minstens deels in België liggen. De wijziging is teruggedraaid.');
              return;
            }
            bumpAddressSelectionRevision();
            try {
              await reRunSpatialQuery(feature);
              queueSaveToBackend();
            } catch (err) {
              console.error('[BB] vertex edit save failed:', err);
            }
            updateBBPanel();
            applyEditStyle(feature);
          });
        });
      });
      
      // Bind delete buttons. We triggeren bij voorkeur upstream's eigen
      // delete-knop in `#draw-summary-panel` - die doet intern niet alleen
      // `removeFeature` maar OOK een `saveToBackend()` POST naar
      // /spatial-search/draw/save. Zonder die call blijft de drawing
      // server-side staan en verschijnt 'ie terug bij heropenen van de map.
      listEl.querySelectorAll('.bb-delete-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const id = parseInt(btn.dataset.id);
          const feature = items[id]?.feature;
          if (!feature || !drawSource) return;

          const upstreamBtn = document.querySelector(
            `#draw-summary-panel .delete-row-btn[data-index="${id}"]`
          );
          if (upstreamBtn) {
            // Klikt op de verborgen upstream-knop → upstream doet removeFeature
            // + saveToBackend. Onze bridge intercepteert de /draw/save call
            // en stuurt postMessage naar de wizard.
            upstreamBtn.click();
            // Onze panel-update gebeurt automatisch via MutationObserver +
            // setInterval, maar voor snelle UX-respons ook direct triggeren.
            setTimeout(updateBBPanel, 100);
          } else {
            // Fallback: directe removeFeature + handmatig een save triggeren
            // via een synthetische post naar /draw/save zou hier handig zijn,
            // maar het is sowieso onverwacht dat upstream-knop ontbreekt.
            console.warn('[BB] upstream delete-row-btn niet gevonden, fallback removeFeature');
            drawSource.removeFeature(feature);
            updateBBPanel();
          }
        });
      });
      
      // Bind zoom buttons
      listEl.querySelectorAll('.bb-zoom-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const id = parseInt(btn.dataset.id);
          const feature = items[id]?.feature;
          if (feature && window.bbMap) {
            const extent = feature.getGeometry().getExtent();
            window.bbMap.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
          }
        });
      });
      
      // Bind name click for inline editing
      listEl.querySelectorAll('.bb-name').forEach(nameEl => {
        nameEl.addEventListener('click', (e) => {
          // Already editing? ignore
          if (nameEl.querySelector('input')) return;
          
          const id = parseInt(nameEl.dataset.id);
          const feature = items[id]?.feature;
          if (!feature) return;
          
          const currentName = feature.get('gebiedNaam') || `Gebied ${feature.get('selectionNumber') ?? id + 1}`;
          
          // Replace with input
          const input = document.createElement('input');
          input.type = 'text';
          input.value = currentName;
          input.style.cssText = 'width:120px;font-size:13px;font-weight:500;padding:2px 4px;border:1px solid #FFD100;border-radius:3px;outline:none;';
          
          nameEl.textContent = '';
          nameEl.appendChild(input);
          input.focus();
          input.select();
          
          const saveName = () => {
            const newName = input.value.trim();
            setFeatureName(feature, newName);
            updateBBPanel();
          };
          
          input.addEventListener('blur', saveName);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              saveName();
            } else if (e.key === 'Escape') {
              updateBBPanel(); // Cancel, restore original
            }
          });
        });
      });
      
      if (items.length > 0) {
        noSelection.style.display = 'none';
        totalCountEl.style.display = 'block';
        totalValueEl.textContent = totalAddresses.toLocaleString();
      } else {
        noSelection.style.display = 'block';
        totalCountEl.style.display = 'none';
      }
      
      // Notify parent
      if (window.parent !== window) {
        const allAddresses = items.flatMap(s => s.addressData || []);
        window.parent.postMessage({
          type: 'gis-selection-update',
          count: totalAddresses,
          addressData: allAddresses
        }, '*');
      }

      // Houd zoom-warning in sync (setInterval kan tussen zoom-events vallen,
      // dan zou de melding niet meer refreshen.)
      if (window.bbMap) {
        updateBBDrawAvailability(window.bbMap);
      }
    }
    
    function confirmBBSelection() {
      const drawSource = getDrawSource();
      if (!drawSource) return;
      
      const features = drawSource.getFeatures();
      let total = 0;
      const allAddresses = [];
      
      features.forEach(f => {
        const data = f.get('addressData') || [];
        total += data.length;
        allAddresses.push(...data);
      });
      
      if (window.parent === window) {
        alert(`Selectie bevestigd: ${total} adressen`);
        return;
      }
      
      window.parent.postMessage({
        type: 'gis-selection-confirmed',
        count: total,
        addressData: allAddresses
      }, '*');
    }
    
    function showBBAddresses() {
      // For now, just alert - could add modal later
      const drawSource = getDrawSource();
      if (!drawSource) return;
      
      const features = drawSource.getFeatures();
      let total = 0;
      features.forEach(f => {
        const data = f.get('addressData') || [];
        total += data.length;
      });
      
      alert(`${total} adressen geselecteerd. Bekijk adressenlijst na bevestigen in campagnetool.`);
    }
    
    async function handleBBSearch(query, map) {
      const resultsEl = document.getElementById('bb-search-results');
      
      if (!query || query.length < 3) {
        resultsEl.style.display = 'none';
        return;
      }
      
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=be&limit=5`;
        const response = await fetch(url);
        const results = await response.json();
        
        if (results.length === 0) {
          resultsEl.innerHTML = '<div style="padding:8px 12px;font-size:12px;">Geen resultaten</div>';
        } else {
          resultsEl.innerHTML = results.map(r => `
            <div class="bb-search-item" data-lon="${r.lon}" data-lat="${r.lat}" 
                 style="padding:8px 12px;cursor:pointer;font-size:12px;border-bottom:1px solid #eee;">
              ${r.display_name}
            </div>
          `).join('');
          
          resultsEl.querySelectorAll('.bb-search-item').forEach(item => {
            item.addEventListener('click', () => {
              const lon = parseFloat(item.dataset.lon);
              const lat = parseFloat(item.dataset.lat);
              map.getView().animate({
                center: fromLonLat([lon, lat]),
                zoom: 15,
                duration: 500
              });
              resultsEl.style.display = 'none';
              document.getElementById('bb-search-input').value = item.textContent.trim().split(',')[0];
            });
          });
        }
        
        resultsEl.style.display = 'block';
      } catch (err) {
        console.error('[BB] Search error:', err);
      }
    }
    
    // Close search when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#bewonersbrief-search')) {
        document.getElementById('bb-search-results').style.display = 'none';
      }
    });
  